'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';

type Mode = 'login' | 'signup';

export default function AuthPanel() {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    fetch('/api/auth/login')
      .then((res) => res.json())
      .then((data) => {
        if (data.authenticated) {
          const redirect = searchParams.get('redirect') || '/upload';
          router.push(redirect);
        }
      })
      .catch(() => {});
  }, [router, searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = mode === 'signup' ? '/api/auth/signup' : '/api/auth/login';
      const body =
        mode === 'signup'
          ? { email, password, name }
          : email.trim()
            ? { email, password }
            : { password };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        const redirect = searchParams.get('redirect') || '/upload';
        router.push(redirect);
        router.refresh();
      } else {
        setError(data.error || 'Authentication failed');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full terminal-border rounded-2xl p-6 sm:p-8 space-y-5">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-white tracking-tight">
          {mode === 'login' ? 'Sign in' : 'Create your profile'}
        </h2>
        <p className="text-sm text-gray-400">
          {mode === 'login'
            ? 'Access your workspace and start enriching lists.'
            : 'Set up an account to upload lists and fund enrichments.'}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-black/40 border border-white/8">
        <button
          type="button"
          onClick={() => setMode('login')}
          className={`py-2.5 rounded-lg text-sm font-medium transition ${
            mode === 'login' ? 'btn-primary shadow-none' : 'text-gray-400 hover:text-white'
          }`}
        >
          Sign in
        </button>
        <button
          type="button"
          onClick={() => setMode('signup')}
          className={`py-2.5 rounded-lg text-sm font-medium transition ${
            mode === 'signup' ? 'btn-primary shadow-none' : 'text-gray-400 hover:text-white'
          }`}
        >
          Create profile
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === 'signup' && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="ui-input w-full px-4 py-3 text-sm"
              placeholder="Your name"
              disabled={loading}
              autoComplete="name"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Email{' '}
            {mode === 'login' && (
              <span className="text-gray-500 font-normal">(or leave blank for site password)</span>
            )}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="ui-input w-full px-4 py-3 text-sm"
            placeholder="you@company.com"
            disabled={loading}
            required={mode === 'signup'}
            autoComplete="email"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="ui-input w-full px-4 py-3 text-sm"
            placeholder={mode === 'signup' ? 'At least 8 characters' : 'Password'}
            autoFocus
            disabled={loading}
            required
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
          />
        </div>

        {error && (
          <div className="p-3 bg-red-950/40 border border-red-500/35 rounded-xl text-red-300 text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !password || (mode === 'signup' && !email)}
          className="btn-primary w-full px-4 py-3 text-sm disabled:opacity-50"
        >
          {loading ? 'Please wait…' : mode === 'signup' ? 'Create profile' : 'Sign in'}
        </button>

        <p className="text-[11px] leading-relaxed text-gray-500 text-center">
          By continuing, you agree to our{' '}
          <Link href="/terms" className="text-[var(--accent)] hover:underline">
            Terms & Conditions
          </Link>{' '}
          and{' '}
          <Link href="/privacy" className="text-[var(--accent)] hover:underline">
            Privacy Policy
          </Link>
          . Enriched contact data may not be used for calling, texting, or marketing without
          required consent.
        </p>
      </form>
    </div>
  );
}
